import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

import awardBackground from "assets/img/awardbackground.png";
import eventLogo from "assets/img/stanys-logo.png";
import { useData } from "context/DataContext";

const TEXT_COLOUR = "#203A5F"; // accessible slate-navy
const SHADOW = "0 2px 4px rgba(0,0,0,0.25)";

/* ------------------------------------------------------------------ */
/* Shared slide wrapper                                               */
/* ------------------------------------------------------------------ */
const CenteredBox = ({ children, sx = {} }) => (
  <Box
    sx={{
      width: "100vw",
      height: "100vh",
      p: 6,
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
      backgroundImage: `url(${awardBackground})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      color: TEXT_COLOUR,
      ...sx,
    }}
  >
    {children}
  </Box>
);

/* ------------------------------------------------------------------ */
/* Individual slide types                                             */
/* ------------------------------------------------------------------ */
const WelcomeSlide = ({ eventName }) => (
  <CenteredBox>
    <img src={eventLogo} alt="Event logo" style={{ maxHeight: 180 }} />

    <Typography
      variant="h1"
      sx={{ mt: 4, fontWeight: 700, textShadow: SHADOW }}
    >
      {eventName}
    </Typography>

    <Typography variant="h4" sx={{ mt: 2 }}>
      Awards Ceremony
    </Typography>
  </CenteredBox>
);

const DivisionTitleSlide = ({ title }) => (
  <CenteredBox>
    <Typography variant="h2" sx={{ fontWeight: 700, textShadow: SHADOW }}>
      {title}
    </Typography>
  </CenteredBox>
);

const AwardSlide = ({ award, projectInfo }) => (
  <CenteredBox>
    <Typography
      variant="h3"
      sx={{
        fontWeight: 700,
        mb: 2,
        // textShadow: SHADOW
      }}
    >
      {award.data.Name}
    </Typography>

    <Divider
      sx={{
        my: 3,
        width: "65%",
        borderColor: TEXT_COLOUR,
        opacity: 0.35,
      }}
    />

    {projectInfo.map(({ title, students }, idx) => (
      <Box key={idx} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {students}
        </Typography>
        <Typography variant="h5">{title}</Typography>
      </Box>
    ))}
  </CenteredBox>
);

const ClosingSlide = () => (
  <CenteredBox>
    <Typography
      variant="h2"
      sx={{ fontWeight: 700, mb: 4, textShadow: SHADOW }}
    >
      Thank You for Joining Us!
    </Typography>

    <Typography variant="h6" sx={{ mb: 1 }}>
      View fair results and project details at:
    </Typography>

    <Typography
      variant="h4"
      component="a"
      href="https://nyssc.most.org/results"
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        color: TEXT_COLOUR,
        fontWeight: 700,
        textDecoration: "underline",
      }}
    >
      nyssc.most.org/results
    </Typography>
  </CenteredBox>
);

/* ------------------------------------------------------------------ */
/* Main container (unchanged logic)                                   */
/* ------------------------------------------------------------------ */
const AwardsPresentation = () => {
  const { awards, projects, fairEvent } = useData();
  const [idx, setIdx] = useState(0);

  /* Project lookup */
  const projectMap = useMemo(
    () =>
      projects.reduce((acc, rec) => {
        acc[rec.id] = {
          title: rec.data.Name || rec.data.Name,
          students: rec.data.rollup_students || "—",
        };
        return acc;
      }, {}),
    [projects]
  );

  /* Build slides in order */
  const slides = useMemo(() => {
    if (!awards.length) return [];

    const juniors = awards.filter((a) =>
      a.data.award_division?.startsWith("Junior")
    );
    const seniors = awards.filter((a) =>
      a.data.award_division?.startsWith("Senior")
    );

    const makeAwardSlide = (aw) => ({
      type: "award",
      award: aw,
      projectInfo: aw.data.fk_project
        .map((pid) => projectMap[pid])
        .filter(Boolean),
    });

    return [
      { type: "welcome" },
      { type: "division", title: "Junior Division (Grades 7-9)" },
      ...juniors.map(makeAwardSlide),
      { type: "division", title: "Senior Division (Grades 10-12)" },
      ...seniors.map(makeAwardSlide),
      { type: "closing" },
    ];
  }, [awards, projectMap]);

  /* Keyboard nav */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight")
        setIdx((i) => Math.min(i + 1, slides.length - 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides.length]);

  /* Fallback while loading */
  if (!slides.length)
    return (
      <CenteredBox>
        <Typography variant="h4">Loading award slides…</Typography>
      </CenteredBox>
    );

  /* Render */
  const slide = slides[idx];
  switch (slide.type) {
    case "welcome":
      return (
        <WelcomeSlide
          eventName={fairEvent?.nyssc_title || "Science Congress"}
        />
      );
    case "division":
      return <DivisionTitleSlide title={slide.title} />;
    case "award":
      return <AwardSlide award={slide.award} projectInfo={slide.projectInfo} />;
    case "closing":
      return <ClosingSlide />;
    default:
      return null;
  }
};

export default AwardsPresentation;
